import { useEffect, useMemo, useState } from 'react';
import { Box, Button, CircularProgress, Stack, Typography, useTheme } from '@mui/material';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import { parseXrkBuffer, sessionToJson } from '@kangaroo/mychron/xrk';
import {
  PageHeader,
  getHeaderChrome,
  headerOutlinedButtonSx,
  headerSectionLabelSx,
} from '@kangaroo/ui';

import { SessionSelectorDrawer } from '../../components/sessions/SessionSelectorDrawer';
import { SessionsCompareMap } from '../../components/sessions/SessionsCompareMap';
import { createCompareSlot, type CompareSlotState } from '../../features/sessions/compareSlots';
import { pickDefaultLapNumber } from '../../features/sessions/sessionContent';
import type { SessionListItem } from '../../features/sessions/sessionList';
import { useSessionLapSlot } from '../../features/sessions/useSessionLapSlot';
import { isMyChronSessionExport, type MyChronSessionExport } from '../../lib/telemetrySession';
import { PAGE_ICONS } from '../../lib/pageIcons';

const DEMO_SESSION_ID = 'a_0180';
const DEMO_SESSION: SessionListItem = {
  id: DEMO_SESSION_ID,
  title: 'June 28, 2026',
  subtitle: '13:30 · CWFULL · 13 laps',
  recordedAtMs: Date.UTC(2026, 5, 28, 13, 30, 10),
  trackName: 'CWFULL',
  driverId: null,
  driverName: 'Demo driver',
  driverAvatarUrl: null,
};

export function PublicSessionDemoPage() {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const headerChrome = getHeaderChrome(isDark);
  const [session, setSession] = useState<MyChronSessionExport | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [primaryLapNumber, setPrimaryLapNumber] = useState<number | null>(null);
  const [compareSlots, setCompareSlots] = useState<CompareSlotState[]>([]);
  const [drawerOpen, setDrawerOpen] = useState(false);

  useEffect(() => {
    let cancelled = false;

    void fetch('/demo/a_0180.xrz')
      .then(async (response) => {
        if (!response.ok) {
          throw new Error(`Unable to load demo session (${response.status})`);
        }

        const payload = sessionToJson(parseXrkBuffer(Buffer.from(await response.arrayBuffer())));
        if (!isMyChronSessionExport(payload)) {
          throw new Error('The demo session is not a valid MyChron export.');
        }

        if (!cancelled) {
          setSession(payload);
          setPrimaryLapNumber(pickDefaultLapNumber(payload));
        }
      })
      .catch((error: unknown) => {
        if (!cancelled) {
          setLoadError(error instanceof Error ? error.message : 'Unable to load the demo session.');
        }
      });

    return () => {
      cancelled = true;
    };
  }, []);

  const primarySlot = useSessionLapSlot({
    sessionId: null,
    sessionExport: session,
    lapTimingMode: 'mychron',
    sessionTrackName: DEMO_SESSION.trackName,
    readOnly: true,
    publicDemo: true,
  });
  const selectionLabel = useMemo(() => {
    const primary = primaryLapNumber == null ? 'Choose lap' : `L${primaryLapNumber}`;
    const comparisons = compareSlots
      .map((slot) => slot.lapNumber == null ? 'Choose lap' : `L${slot.lapNumber}`)
      .join(' vs ');
    return comparisons ? `${primary} vs ${comparisons}` : `${primary} · ${DEMO_SESSION.subtitle}`;
  }, [compareSlots, primaryLapNumber]);

  function resetWorkspace() {
    setPrimaryLapNumber(pickDefaultLapNumber(session));
    setCompareSlots([]);
  }

  return (
    <Box sx={{ height: '100dvh', minHeight: 560, display: 'flex', flexDirection: 'column', p: { xs: 0, md: 2 }, bgcolor: 'background.default' }}>
      <Box sx={{ px: { xs: 1, md: 0 }, pt: { xs: 0.75, md: 0 } }}>
      <PageHeader
        title="Sessions"
        subtitle="Review uploaded MyChron sessions, laps, and notes."
        icon={PAGE_ICONS.sessions}
        mb={1}
        actions={(
          <Stack direction="row" spacing={1.25} alignItems="center" sx={{ minWidth: 0, justifyContent: 'flex-end' }}>
            <Typography sx={{ ...headerSectionLabelSx(isDark), display: { xs: 'none', md: 'block' } }}>
              Session & laps
            </Typography>
            <Button
              variant="outlined"
              size="small"
              aria-label="Choose session and laps"
              disabled={!session}
              onClick={() => setDrawerOpen(true)}
              endIcon={<KeyboardArrowDownIcon sx={{ fontSize: 20 }} />}
              sx={{
                height: 40,
                minWidth: { xs: 180, sm: 260 },
                maxWidth: 420,
                px: 1.3,
                textTransform: 'none',
                fontWeight: 800,
                justifyContent: 'space-between',
                ...headerOutlinedButtonSx(isDark),
              }}
            >
              <Stack spacing={0.2} sx={{ minWidth: 0, alignItems: 'flex-start', overflow: 'hidden' }}>
                <Typography sx={{ width: '100%', fontSize: '0.82rem', fontWeight: 850, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {DEMO_SESSION.title}
                </Typography>
                <Typography sx={{ width: '100%', fontSize: '0.66rem', color: headerChrome.buttonSecondary, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {selectionLabel}
                </Typography>
              </Stack>
            </Button>
            <Button
              href="/register"
              target="_top"
              variant="contained"
              size="small"
              endIcon={<ArrowForwardIcon sx={{ fontSize: 15 }} />}
              sx={{ height: 40, px: 1.25, borderRadius: '6px', textTransform: 'none', fontWeight: 850, whiteSpace: 'nowrap' }}
            >
              Use your data
            </Button>
          </Stack>
        )}
      />
      </Box>

      <Box sx={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column', overflow: 'hidden', borderRadius: { xs: 0, md: '8px' } }}>
        {loadError ? (
          <Box sx={{ flex: 1, display: 'grid', placeItems: 'center', px: 3 }}>
            <Typography color="error" textAlign="center">{loadError}</Typography>
          </Box>
        ) : !session || primaryLapNumber == null ? (
          <Box sx={{ flex: 1, display: 'grid', placeItems: 'center', bgcolor: '#020A12' }}>
            <Stack spacing={1.25} alignItems="center">
              <CircularProgress size={28} />
              <Typography sx={{ color: '#A6B0BE', fontSize: '0.78rem' }}>Loading the MyChron session</Typography>
            </Stack>
          </Box>
        ) : (
          <SessionsCompareMap
            publicDemo
            viewKey={`${DEMO_SESSION_ID}:mychron:${primaryLapNumber}:${compareSlots.map((slot) => `${slot.id}:${slot.lapNumber}`).join('|')}`}
            primarySessionId={DEMO_SESSION_ID}
            primarySessionExport={session}
            primaryLapNumber={primaryLapNumber}
            onPrimaryLapChange={setPrimaryLapNumber}
            lapTimingMode="mychron"
            primarySlot={primarySlot}
            compareSlots={compareSlots}
            sessions={[DEMO_SESSION]}
          />
        )}
      </Box>

      <SessionSelectorDrawer
        publicDemo
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        sessions={[DEMO_SESSION]}
        primarySessionId={DEMO_SESSION_ID}
        primarySessionExport={session}
        primaryLapNumber={primaryLapNumber}
        lapTimingMode="mychron"
        onLapTimingModeChange={() => {}}
        primarySlot={primarySlot}
        onSessionSelectionChange={() => {}}
        onPrimaryLapChange={setPrimaryLapNumber}
        compareSlots={compareSlots}
        onAddCompareSlot={() => setCompareSlots((current) => [...current, createCompareSlot(DEMO_SESSION_ID)])}
        onRemoveCompareSlot={(slotId) => setCompareSlots((current) => current.filter((slot) => slot.id !== slotId))}
        onCompareSlotSessionChange={() => {}}
        onCompareSlotLapChange={(slotId, lapNumber) => setCompareSlots((current) => current.map((slot) => slot.id === slotId ? { ...slot, lapNumber } : slot))}
        compareSessionGroups={{ matching: [DEMO_SESSION], other: [] }}
        onReset={resetWorkspace}
        onSessionContextSaved={() => {}}
      />
    </Box>
  );
}
